/**
 * The product's words for money and collection work, decided once
 * (docs/product/customer-360/02-RESEARCH-AND-REDESIGN.md §19.1, owner
 * checkpoint 1, 23 Oct 2026). Every screen reads them from here; none decides
 * them for itself.
 *
 *   Balance       the current accounting position (TallyPrime's, or the sheet's)
 *   Overdue       money past an actual due date — and only money
 *   Not yet due   money whose due date has not passed
 *   Age           time since the bill (or reference) date
 *   Late          a collection follow-up whose scheduled date has passed
 *   Grace         an operational tolerance after the due date; the money stays overdue
 *   Critical age  Timely's prioritisation threshold, read from age; never due-date semantics
 *   Slow payer    the payment rank stored as `Late`, set from age; the stored value is unchanged
 *
 * Never mixed:
 *   - an age (however old) is not overdue, and a threshold is not overdue;
 *   - a late follow-up is not overdue money, and the balance of accounts with
 *     late follow-ups is never presented as overdue receivables;
 *   - overdue is never inferred where the source cannot tell it: a sheet says
 *     "Overdue: not available from this sheet", and a bill with no due date
 *     counts only where the company explicitly chose to read it as due on its
 *     bill date (02 F3, rule 2 of 23 Oct).
 */
import type { AccountStatement, StatementBill } from './accountStatement.js';
import type { ActivityEntry, ActivityKind } from './activity.js';
import type { Cheque } from './cheques.js';
import { chequeState, CHEQUE_STATES } from './cheques.js';
import type { PriorityReason } from './collectionPriority.js';
import { dialableNumber } from './contacts.js';
import type { BalanceType, Customer, PaymentRank } from './customer.js';
import type { FollowUpCategory } from './followUp.js';
import { overdueAgeing } from './money.js';
import { fromPaise, toPaise } from './receivablesFacts.js';
import type { ReceivablesFreshness } from './receivablesFreshness.js';
import type { ReceivablesSettings } from './receivablesSettings.js';
import type { CheckCode } from './sourceChecks.js';

/* ---------------------------------- words ---------------------------------- */

export const TERMS = {
    balance: 'Balance',
    overdue: 'Overdue',
    notYetDue: 'Not yet due',
    age: 'Age',
    late: 'Late',
    grace: 'Grace',
    criticalAge: 'Critical age',
    slowPayer: 'Slow payer',
} as const;

export type Term = keyof typeof TERMS;

/** What each term means, in the words a help text or a caption uses. */
export const TERM_MEANINGS: Record<Term, string> = {
    balance: 'The current accounting position.',
    overdue: 'Money past an actual due date.',
    notYetDue: 'Money whose due date has not passed.',
    age: 'Time since the bill date.',
    late: 'A follow-up whose date has passed.',
    grace: 'Days your company waits after a due date before chasing. The money is still overdue.',
    criticalAge: 'Timely’s line for old money, read from its age. It is not a due date.',
    slowPayer: 'Set automatically when money on the account is more than 45 days old.',
};

/** A payment rank in words. `Late` is stored and stays; "Slow payer" is what it is called (decided 23 Oct 2026). */
export const RANK_WORDS: Record<PaymentRank, string> = { Good: 'Good payer', Late: 'Slow payer', Bad: 'Bad debt' };

/** The automatic rank, as a rank control offers it: "Automatic — slow payer". */
export const automaticRankWords = (rank: PaymentRank): string => `Automatic — ${RANK_WORDS[rank].toLowerCase()}`;

/** A follow-up's state in one word: "Late", never "Overdue" — overdue is money. */
export const FOLLOW_UP_WORDS: Record<FollowUpCategory, string> = {
    overdue: 'Late',
    today: 'Due today',
    future: 'Scheduled',
    no_follow_up: 'Not set',
    completed: 'Collected',
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** "Late 1 day", "Late 20 days". */
export const lateFor = (days: number): string => `Late ${plural(Math.max(1, Math.abs(days)), 'day')}`;

export type FollowUpTone = 'late' | 'today' | 'scheduled' | 'not_set' | 'collected';

export interface FollowUpWords {
    /** As a list's cell says it: "Late 3 days", "Today", "Fri 23 Oct". */
    text: string;
    /** As a screen reader hears it beside the account's name. */
    spoken: string;
    /** As a heading's tail says it: "Next follow-up · 2 days late". */
    timing: string;
    tone: FollowUpTone;
}

/**
 * A next follow-up, in words, from its category and how many days away it is
 * (negative once passed). `when` is the date as the company writes it.
 */
export function followUpWords(category: FollowUpCategory, daysToFollowUp: number | null, when: string): FollowUpWords {
    switch (category) {
        case 'completed': return { text: 'Collected', spoken: 'collected', timing: 'collected', tone: 'collected' };
        case 'no_follow_up': return { text: 'Not set', spoken: 'not set', timing: 'not set', tone: 'not_set' };
        case 'today': return { text: 'Today', spoken: 'due today', timing: 'today', tone: 'today' };
        case 'overdue': {
            const days = Math.max(1, Math.abs(daysToFollowUp ?? 1));
            const text = lateFor(days);
            return { text, spoken: text.toLowerCase(), timing: `${plural(days, 'day')} late`, tone: 'late' };
        }
        default: {
            const text = daysToFollowUp === 1 ? 'Tomorrow' : when;
            return { text, spoken: `on ${text}`, timing: daysToFollowUp === 1 ? 'tomorrow' : when, tone: 'scheduled' };
        }
    }
}

/* ------------------------------ critical age ------------------------------- */

/** The ages an account's bands speak for, youngest first. */
const STANDARD_AGES = [45, 90, 135] as const;

/**
 * The standard age that stands for the company's critical threshold: the first
 * band that lies wholly past it, as the reasons read it. Critical age is an
 * age — never a number of days past a due date (02 §19.9 C1).
 */
export const criticalAgeBand = (criticalDays: number): 45 | 90 | 135 =>
    STANDARD_AGES.find(age => age >= criticalDays) ?? 135;

/* -------------------------- bills with no due date -------------------------- */

/**
 * How a company reads a bill TallyPrime gave no due date (02 F3; 01 §2.2):
 *   - `bill_date`  — the company chose "due on the bill date": such bills count as overdue from their bill date;
 *   - `not_aged`   — the company chose to leave them out;
 *   - `not_chosen` — nobody chose. The product's default is not a choice: such bills are never counted as overdue.
 *
 * The choice is offered only with ageing by due date, so it is a choice only in a saved version that ages by due
 * date. With ageing by bill date the setting is never shown, and whatever it holds was not chosen.
 */
export type NoDueDateRule = 'bill_date' | 'not_aged' | 'not_chosen';

export function noDueDateRule(saved: { version: number; settings: Pick<ReceivablesSettings, 'ageing'> }): NoDueDateRule {
    if (saved.version > 0 && saved.settings.ageing.basis === 'due_date') {
        return saved.settings.ageing.missingDueDate === 'use_bill_date' ? 'bill_date' : 'not_aged';
    }
    return 'not_chosen';
}

/**
 * The settings overdue is worked out with: the company's own, except that a bill with no due date is due on its bill
 * date only where the company chose that. Ageing by bill date never reads due dates, so nothing about age moves.
 */
export function overdueSettings(settings: ReceivablesSettings, rule: NoDueDateRule): ReceivablesSettings {
    if (rule === 'bill_date' || settings.ageing.missingDueDate === 'unaged') return settings;
    return { ...settings, ageing: { ...settings.ageing, missingDueDate: 'unaged' } };
}

/* --------------------------------- lateness -------------------------------- */

/** How late the overdue is, in days past the due date: 1–30, 31–60, 61–90, over 90 (02 §5.5). */
export const LATENESS_BOUNDS: readonly number[] = [30, 60, 90];
export const LATENESS_LABELS: readonly string[] = ['1–30 days late', '31–60 days late', '61–90 days late', 'Over 90 days late'];

export const latenessBand = (daysPastDue: number): number => {
    for (let i = 0; i < LATENESS_BOUNDS.length; i++) if (daysPastDue <= LATENESS_BOUNDS[i]!) return i;
    return LATENESS_BOUNDS.length;
};

/* --------------------------------- overdue --------------------------------- */

export interface AmountCount { amount: number; bills: number }

export interface BillsOverdue {
    kind: 'bills';
    /** TallyPrime's closing balance, signed (+ = Dr). */
    balance: number;
    /** Past an actual due date, grace included. */
    overdue: AmountCount & {
        /** The most days any of it is past its due date; null when none is. */
        oldestDays: number | null;
        /** The part on bills with no due date, counted from the bill date because the company chose that. */
        fromBillDate: number;
    };
    /** The part of the overdue inside the company's grace: still overdue, shown apart (rule 3). */
    grace: AmountCount & { days: number };
    /** Due today or later. `next` is the earliest due date still to come (today included). */
    notYetDue: AmountCount & { next: { amount: number; date: string; bills: number } | null };
    /** On bills with no due date, not counted as overdue (the company did not choose to read them by bill date). */
    noDueDate: AmountCount & { rule: NoDueDateRule };
    /** Owed against no bill: no due date, so never overdue. */
    unbilled: { amount: number; reason: 'on_account' | 'not_bill_wise' } | null;
    credits: number;
    /** The overdue by how late it is, in the four lateness bands. */
    lateness: AmountCount[];
}

/**
 * One account's answer to "how much is actually overdue?" (01 §2.2):
 *   - `bills`     TallyPrime, with bills: worked out from them;
 *   - `no_bills`  TallyPrime keeps this balance without bills: overdue cannot be known;
 *   - `sheet`     a receivables sheet: its due dates are not read, so overdue is not available — only its age;
 *   - `credit`    in credit: nothing to collect;
 *   - `settled`   nothing owed (bills may still be open on a nil balance);
 *   - `unread`    a TallyPrime account whose bills have not been read yet.
 */
export type OverdueAnswer =
    | BillsOverdue
    | { kind: 'no_bills'; balance: number }
    | { kind: 'sheet'; balance: number; pastCriticalAge: { amount: number; overDays: 45 | 90 | 135 } }
    | { kind: 'credit'; credit: number }
    | { kind: 'settled'; openBills: boolean }
    | { kind: 'unread'; balance: number };

const sumOf = (bills: readonly StatementBill[]): number => fromPaise(bills.reduce((s, b) => s + toPaise(b.pending), 0));

/** The overdue answer from an account's statement, built with `overdueSettings` (so no-due-date bills follow the rule). */
export function billsOverdue(statement: AccountStatement, rule: NoDueDateRule, graceDays: number): BillsOverdue {
    const past = statement.bills.filter(b => b.standing.kind === 'overdue' || b.standing.kind === 'grace');
    const grace = statement.bills.filter(b => b.standing.kind === 'grace');
    const ahead = statement.bills.filter(b => b.standing.kind === 'due_today' || b.standing.kind === 'not_due');
    const noDue = statement.bills.filter(b => b.standing.kind === 'no_due_date');
    const daysPast = (b: StatementBill) => (b.standing.kind === 'overdue' || b.standing.kind === 'grace' ? b.standing.days : 0);

    const nextDate = ahead.map(b => b.dueDate ?? '').filter(Boolean).sort()[0] ?? null;
    const onNext = nextDate ? ahead.filter(b => b.dueDate === nextDate) : [];

    const bands = LATENESS_LABELS.map(() => ({ paise: 0, bills: 0 }));
    for (const b of past) {
        const band = bands[latenessBand(daysPast(b))]!;
        band.paise += toPaise(b.pending);
        band.bills += 1;
    }

    return {
        kind: 'bills',
        balance: statement.totals.balance,
        overdue: {
            amount: sumOf(past),
            bills: past.length,
            oldestDays: past.length ? Math.max(...past.map(daysPast)) : null,
            fromBillDate: sumOf(past.filter(b => b.dueDate === null)),
        },
        grace: { amount: sumOf(grace), bills: grace.length, days: graceDays },
        notYetDue: {
            amount: sumOf(ahead),
            bills: ahead.length,
            next: nextDate ? { amount: sumOf(onNext), date: nextDate, bills: onNext.length } : null,
        },
        noDueDate: { amount: sumOf(noDue), bills: noDue.length, rule },
        unbilled: statement.unbilledDebit,
        credits: statement.totals.credits,
        lateness: bands.map(b => ({ amount: fromPaise(b.paise), bills: b.bills })),
    };
}

type OverdueCustomer = Pick<Customer, 'total' | 'totalType' | 'ageing' | 'ageingTypes' | 'over90' | 'over90Type' | 'dueOver45' | 'dueOver45Type'>;

/** One account's overdue answer, from its source, its balance and (for TallyPrime) its statement. */
export function accountOverdue(input: {
    customer: OverdueCustomer;
    source: 'tally' | 'sheet';
    statement: AccountStatement | null;
    rule: NoDueDateRule;
    graceDays: number;
    criticalDays: number;
}): OverdueAnswer {
    const { customer, statement } = input;
    const total = Math.abs(Number(customer.total) || 0);
    if (customer.totalType === 'Cr' && total > 0) return { kind: 'credit', credit: total };
    if (total === 0) {
        // Nil, yet TallyPrime may still hold bills on it that net to nothing: the statement says so; before it is read,
        // the windows stored on the account do (the accounting check's own words, "balance nil, bills open").
        const w = customer.ageing?.windows;
        const open = statement
            ? statement.bills.length > 0 || statement.credits.length > 0
            : !!w && (w.amounts.some(a => a !== 0) || w.notYetDue !== 0 || w.notAged !== 0 || w.credits !== 0);
        return { kind: 'settled', openBills: open };
    }
    if (input.source === 'sheet') {
        const over = criticalAgeBand(input.criticalDays);
        const aged = overdueAgeing(customer);
        const amount = over === 45 ? aged.over45 : over === 90 ? aged.over90 : aged.over135;
        return { kind: 'sheet', balance: total, pastCriticalAge: { amount, overDays: over } };
    }
    if (!statement) return { kind: 'unread', balance: total };
    if (!statement.billWise) return { kind: 'no_bills', balance: total };
    return billsOverdue(statement, input.rule, input.graceDays);
}

/** The overdue figure where it is known (zero included); null where it cannot be known. */
export const knownOverdue = (answer: OverdueAnswer): number | null =>
    answer.kind === 'bills' ? answer.overdue.amount : answer.kind === 'credit' || answer.kind === 'settled' ? 0 : null;

export interface MoneyWords {
    money: (amount: number) => string;
    /** A calendar day as the company writes it, with its weekday where it helps ("Mon 26 Oct"). */
    day: (date: string) => string;
}

/** What a line of the overdue answer is about, so a screen can mark it (the due-status colours) without re-wording it. */
export type OverdueLineKey =
    | 'overdue' | 'nothing_past_due' | 'grace' | 'from_bill_date' | 'not_yet_due' | 'no_due_date' | 'unbilled'
    | 'not_known' | 'critical_age' | 'credit' | 'settled';

export interface OverdueLine {
    key: OverdueLineKey;
    text: string;
    /**
     * The amount the line opens with, exactly as `text` begins, so a screen can set it in figures without reading the
     * words — in any currency and any language.
     */
    lead?: string;
}

export interface OverdueLines {
    headline: OverdueLine;
    lines: OverdueLine[];
    /** Whether overdue is known here (zero included); false where it cannot be. */
    known: boolean;
}

/** A line that opens with an amount: the amount, then the rest of the sentence. */
const led = (key: OverdueLineKey, amount: string, rest: string): OverdueLine => ({ key, text: `${amount}${rest}`, lead: amount });

/**
 * The overdue answer in its lines, as the account's band says them (02 §5.1). Never "Nothing" while any bill is past
 * its due date; never a figure where overdue cannot be known.
 */
export function overdueLines(answer: OverdueAnswer, w: MoneyWords): OverdueLines {
    switch (answer.kind) {
        case 'bills': {
            const o = answer.overdue;
            const lines: OverdueLine[] = [];
            const headline = o.amount > 0
                ? led('overdue', w.money(o.amount), ` overdue · ${o.bills === 1 ? `1 bill, ${plural(o.oldestDays ?? 0, 'day')} past due` : `${plural(o.bills, 'bill')}, the oldest ${plural(o.oldestDays ?? 0, 'day')} past due`}`)
                : { key: 'nothing_past_due' as const, text: 'Nothing past its due date' };
            if (answer.grace.amount > 0) lines.push(led('grace', w.money(answer.grace.amount), ` of it inside your ${plural(answer.grace.days, 'day')}’ grace`));
            if (o.fromBillDate > 0) lines.push({ key: 'from_bill_date', text: `Includes ${w.money(o.fromBillDate)} on bills with no due date, counted from the bill date (your setting)` });
            const n = answer.notYetDue;
            if (n.amount > 0) {
                lines.push(led('not_yet_due', w.money(n.amount), ` not yet due${n.next ? ` · next ${w.money(n.next.amount)} on ${w.day(n.next.date)}` : ''}`));
            }
            if (answer.noDueDate.amount > 0) {
                lines.push(led('no_due_date', w.money(answer.noDueDate.amount), answer.noDueDate.rule === 'not_chosen'
                    ? ' is on bills with no due date: not counted as overdue'
                    : ' is on bills with no due date'));
            }
            if (answer.unbilled) lines.push(led('unbilled', w.money(answer.unbilled.amount), ' is not against a bill, so it has no due date'));
            return { headline, lines, known: true };
        }
        case 'no_bills':
            return {
                headline: { key: 'not_known', text: 'Overdue: not known' },
                lines: [{ key: 'not_known', text: 'TallyPrime keeps this balance without bills, so Timely can’t say what is overdue' }],
                known: false,
            };
        case 'sheet':
            return {
                headline: { key: 'not_known', text: 'Overdue: not available from this sheet' },
                lines: [{
                    key: 'critical_age',
                    text: answer.pastCriticalAge.amount > 0
                        ? `Past critical age · over ${answer.pastCriticalAge.overDays} days ${w.money(answer.pastCriticalAge.amount)}, from the sheet’s ageing columns`
                        : `Nothing past critical age (over ${answer.pastCriticalAge.overDays} days), by the sheet’s ageing columns`,
                }],
                known: false,
            };
        case 'credit':
            return { headline: { key: 'credit', text: `In credit ${w.money(answer.credit)}` }, lines: [{ key: 'credit', text: 'Nothing on this account is owed' }], known: true };
        case 'settled':
            return {
                headline: { key: 'settled', text: 'Nothing owed' },
                lines: answer.openBills ? [{ key: 'settled', text: 'TallyPrime still holds bills on a nil balance: they net to nothing' }] : [],
                known: true,
            };
        case 'unread':
            return {
                headline: { key: 'not_known', text: 'Overdue: not read yet' },
                lines: [{ key: 'not_known', text: 'TallyPrime’s bills appear after the next update' }],
                known: false,
            };
    }
}

/** The lines' words alone, for a summary or a test. */
export const overdueTexts = (l: OverdueLines): { headline: string; lines: string[] } =>
    ({ headline: l.headline.text, lines: l.lines.map(x => x.text) });

/**
 * One row of the account's money, set as a table is read (Customer 360 polish, 24 Oct 2026): what it is, the amount
 * in figures, and a few words after it. The same facts as `overdueLines`, by the same rules, laid out so the amounts
 * scan: "Overdue · ₹4,20,000 · 1 bill, 160 days past due". A row with no amount states its fact in `text` alone
 * ("Overdue · Not available from this sheet").
 */
export interface MoneyFact {
    key: OverdueLineKey;
    label: string;
    amount?: string;
    text: string;
    /** `dang` for money past its due date; `muted` for a fact that is an absence ("Not available from this sheet"). */
    tone?: 'dang' | 'muted';
}

export interface MoneyFacts {
    rows: MoneyFact[];
    /** What qualifies the rows, said once beneath them, quietly. */
    notes: string[];
    known: boolean;
}

export function moneyFacts(answer: OverdueAnswer, w: MoneyWords): MoneyFacts {
    switch (answer.kind) {
        case 'bills': {
            const o = answer.overdue;
            const rows: MoneyFact[] = [o.amount > 0
                ? {
                    key: 'overdue', label: 'Overdue', amount: w.money(o.amount), tone: 'dang',
                    text: o.bills === 1 ? `1 bill, ${plural(o.oldestDays ?? 0, 'day')} past due` : `${plural(o.bills, 'bill')}, the oldest ${plural(o.oldestDays ?? 0, 'day')} past due`,
                }
                : { key: 'nothing_past_due', label: 'Overdue', text: 'Nothing past its due date' }];
            if (answer.grace.amount > 0) {
                rows.push({ key: 'grace', label: 'In grace', amount: w.money(answer.grace.amount), text: `of the overdue, inside your ${plural(answer.grace.days, 'day')}’ grace` });
            }
            const n = answer.notYetDue;
            if (n.amount > 0) rows.push({ key: 'not_yet_due', label: 'Not yet due', amount: w.money(n.amount), text: n.next ? `next ${w.money(n.next.amount)} on ${w.day(n.next.date)}` : '' });
            if (answer.noDueDate.amount > 0) {
                rows.push({ key: 'no_due_date', label: 'No due date', amount: w.money(answer.noDueDate.amount), text: answer.noDueDate.rule === 'not_chosen' ? 'not counted as overdue' : '' });
            }
            if (answer.unbilled) rows.push({ key: 'unbilled', label: 'Not against a bill', amount: w.money(answer.unbilled.amount), text: 'so it has no due date' });
            const notes = o.fromBillDate > 0 ? [`The overdue includes ${w.money(o.fromBillDate)} on bills with no due date, counted from the bill date (your setting).`] : [];
            return { rows, notes, known: true };
        }
        case 'no_bills':
            return {
                rows: [{ key: 'not_known', label: 'Overdue', text: 'Not known', tone: 'muted' }],
                notes: ['TallyPrime keeps this balance without bills, so Timely can’t say what is overdue.'],
                known: false,
            };
        case 'sheet': {
            const old = answer.pastCriticalAge;
            return {
                rows: [
                    { key: 'not_known', label: 'Overdue', text: 'Not available from this sheet', tone: 'muted' },
                    old.amount > 0
                        ? { key: 'critical_age', label: `Over ${old.overDays} days old`, amount: w.money(old.amount), text: '' }
                        : { key: 'critical_age', label: `Over ${old.overDays} days old`, text: 'Nothing' },
                ],
                notes: ['Ages come from the sheet’s ageing columns: an age, not a due date.'],
                known: false,
            };
        }
        case 'credit':
            return { rows: [], notes: ['Held for the customer: nothing on this account is owed.'], known: true };
        case 'settled':
            return { rows: [], notes: answer.openBills ? ['TallyPrime still holds bills on a nil balance: they net to nothing.'] : [], known: true };
        case 'unread':
            return {
                rows: [{ key: 'not_known', label: 'Overdue', text: 'Not read yet', tone: 'muted' }],
                notes: ['TallyPrime’s bills appear after the next update.'],
                known: false,
            };
    }
}

/* ------------------------------- the attention ------------------------------ */

/**
 * The one "Needs attention" signal on an account and the full list behind "Why this account" (01 §4.2), most
 * serious first. Ordinary overdue (answer 2), a follow-up due today (answer 5) and a promise not yet due (answer 4)
 * are never on the line; their reasons stay in the list.
 */
export type AttentionCode =
    | 'source_attention' | 'stale' | 'balance_review' | 'cheque_bounced' | 'promise_not_kept' | 'promise_missed'
    | 'past_critical_age' | 'no_contact' | 'over_credit_limit' | 'nil_balance_bills_open';

/** What the colour of an attention item means: a failure or broken commitment, something waiting, or an age. */
export type AttentionTone = 'crimson' | 'amber' | 'navy';

export interface AttentionItem { code: AttentionCode | PriorityReason['code']; text: string; tone: AttentionTone; exception: boolean }

const ATTENTION_ORDER: readonly AttentionCode[] = [
    'source_attention', 'stale', 'balance_review', 'cheque_bounced', 'promise_not_kept', 'promise_missed',
    'past_critical_age', 'no_contact', 'over_credit_limit', 'nil_balance_bills_open',
];

const REASON_TONE: Partial<Record<PriorityReason['code'], AttentionTone>> = {
    promise_not_kept: 'crimson', cheque_bounced: 'crimson', promise_missed: 'amber', critical_overdue: 'navy', overdue: 'navy',
    follow_up_overdue: 'amber', follow_up_today: 'amber', promise_due: 'amber', no_contact: 'amber', cheque_to_bank: 'amber',
    high_value: 'navy',
};

/** What a reason's colour means wherever it is drawn — the account's line, a list's row: crimson, amber or navy. */
export const reasonTone = (reason: Pick<PriorityReason, 'code'>): AttentionTone => REASON_TONE[reason.code] ?? 'amber';

/** Which of the reasons engine's reasons are exceptions on the account's line, and under which code. */
const REASON_EXCEPTION: Partial<Record<PriorityReason['code'], AttentionCode>> = {
    cheque_bounced: 'cheque_bounced', promise_not_kept: 'promise_not_kept', promise_missed: 'promise_missed',
    critical_overdue: 'past_critical_age', no_contact: 'no_contact',
};

export interface AttentionInput {
    /** The source's own signal, in the words the person may read (tier 1, 01 §6.1); null when there is none. */
    source: { code: 'source_attention' | 'stale'; text: string } | null;
    /** "Accounting balance needs review": only for people who may read the findings (Phase A). */
    balanceReview: string | null;
    /** The reasons engine's reasons (the same words as Today), most serious first. */
    reasons: readonly PriorityReason[];
    /** Owed beyond the credit limit, as words, when it is. */
    overCreditLimit: string | null;
    /** TallyPrime holds bills on a nil balance. */
    nilBalanceBillsOpen: boolean;
}

export interface Attention {
    /** The one line: the most serious exception, or null. */
    line: AttentionItem | null;
    /** Every item, exceptions first (in their order), then the other reasons. */
    items: AttentionItem[];
    /** When nothing is an exception but there are reasons: the first, said quietly. */
    quiet: AttentionItem | null;
}

export function accountAttention(input: AttentionInput): Attention {
    const exceptions: AttentionItem[] = [];
    const others: AttentionItem[] = [];
    if (input.source) {
        exceptions.push({ code: input.source.code, text: input.source.text, tone: input.source.code === 'source_attention' ? 'crimson' : 'amber', exception: true });
    }
    if (input.balanceReview) exceptions.push({ code: 'balance_review', text: input.balanceReview, tone: 'amber', exception: true });
    for (const r of input.reasons) {
        const code = REASON_EXCEPTION[r.code];
        // Critical age is an exception only at its high level (the engine's own judgement of "critical").
        const exception = code !== undefined && (r.code !== 'critical_overdue' || r.level === 'high');
        const item = { code: exception ? code! : r.code, text: r.text, tone: reasonTone(r), exception };
        (exception ? exceptions : others).push(item);
    }
    if (input.overCreditLimit) exceptions.push({ code: 'over_credit_limit', text: input.overCreditLimit, tone: 'amber', exception: true });
    if (input.nilBalanceBillsOpen) {
        exceptions.push({ code: 'nil_balance_bills_open', text: 'Bills are open on a nil balance', tone: 'amber', exception: true });
    }
    const rank = (i: AttentionItem) => ATTENTION_ORDER.indexOf(i.code as AttentionCode);
    exceptions.sort((a, b) => rank(a) - rank(b));
    const items = [...exceptions, ...others];
    return { line: exceptions[0] ?? null, items, quiet: exceptions.length === 0 ? others[0] ?? null : null };
}

/* ------------------------------ tier-1 signals ------------------------------ */

/**
 * The accounting checks whose open finding on an account makes its balance or open bills uncertain: "Accounting
 * balance needs review" (01 §6.2). Read in Phase A only by people who may read the findings; B1 gives everyone the
 * signal, without the diagnostic behind it.
 */
export const BALANCE_REVIEW_CHECKS: readonly CheckCode[] = [
    'bill_pending_again', 'cheque_bounced_receipt_stands', 'balance_nil_bills_open', 'allocation_unclear',
    'movement_not_explained', 'timely_allocation_needs_review',
];

/**
 * The source's own signal, in tier-1 words (01 §6.1): what everyone who may open the account is told. People who may
 * read the receivables data get the first half and a link to the details, in place of the advice to ask them.
 */
export function sourceSignal(
    freshness: Pick<ReceivablesFreshness, 'level' | 'problem' | 'source'> | null,
    opts: { holder: boolean; age: string | null },
): { code: 'source_attention' | 'stale'; text: string } | null {
    if (!freshness) return null;
    if (freshness.problem) {
        return {
            code: 'source_attention',
            text: opts.holder ? 'Source needs attention — figures are not updating.'
                : 'Source needs attention — figures are not updating. Check amounts with your accounts team.',
        };
    }
    if (freshness.level === 'stale') {
        const from = freshness.source === 'google_sheets' ? 'Sheet' : 'TallyPrime';
        return { code: 'stale', text: `Figures may be stale — ${from} figures from ${opts.age ?? 'some time ago'}.` };
    }
    return null;
}

/** How long ago, roughly, for a warning: "12 minutes ago", "5 hours ago", "3 days ago". */
export const agoWords = (minutes: number): string =>
    minutes < 60 ? `${plural(Math.max(1, Math.round(minutes)), 'minute')} ago`
        : minutes < 1440 ? `${plural(Math.floor(minutes / 60), 'hour')} ago`
            : `${plural(Math.floor(minutes / 1440), 'day')} ago`;

/** "Accounting balance needs review", in its tier-1 words. */
export const balanceReviewWords = (holder: boolean): string => (holder
    ? 'Accounting balance needs review.'
    : 'Accounting balance needs review — check with your accounts team before quoting an amount.');

/* ----------------------------- the work panel ------------------------------- */

export interface ContactToCall { name: string; role: string | null; number: string; dial: string }

/**
 * Who Call rings: the first contact with a dialable number in the account's own order — the source's contact first,
 * then the people the team added. No "primary contact" is invented (01 §5; B3 adds a chosen one).
 */
export function firstDialable(customer: Pick<Customer, 'contactPerson' | 'contactNumber' | 'contactPost' | 'additionalContacts'>): ContactToCall | null {
    const people: { name: string; role: string | null; phone: string }[] = [
        { name: customer.contactPerson || '', role: customer.contactPost || null, phone: customer.contactNumber || '' },
        ...(customer.additionalContacts ?? []).map(p => ({ name: p.name || '', role: p.role || null, phone: p.phone || '' })),
    ];
    for (const p of people) {
        const dial = p.phone ? dialableNumber(p.phone) : null;
        if (dial) return { name: p.name, role: p.role, number: p.phone, dial };
    }
    return null;
}

/** The figure the work panel leads with: what there is to discuss, never more than the balance (01 §5). */
export type ToDiscuss =
    | { kind: 'overdue'; amount: number }
    | { kind: 'owed'; amount: number; note: 'nothing past due' | 'overdue not available' | 'overdue not known' }
    | { kind: 'credit'; amount: number }
    | { kind: 'nothing' };

export function toDiscuss(answer: OverdueAnswer, balance: number): ToDiscuss {
    switch (answer.kind) {
        case 'bills':
            return answer.overdue.amount > 0
                ? { kind: 'overdue', amount: Math.min(answer.overdue.amount, Math.max(balance, 0)) }
                : { kind: 'owed', amount: balance, note: 'nothing past due' };
        case 'sheet': return { kind: 'owed', amount: answer.balance, note: 'overdue not available' };
        case 'no_bills':
        case 'unread': return { kind: 'owed', amount: answer.balance, note: 'overdue not known' };
        case 'credit': return { kind: 'credit', amount: answer.credit };
        case 'settled': return { kind: 'nothing' };
    }
}

/* ------------------------------ record outcome ------------------------------ */

export interface AmountChoice { key: 'overdue' | 'all' | 'critical_age'; label: string; amount: number }

/**
 * The amounts Record outcome offers (01 §7): the overdue (when known and above zero, never more than the balance),
 * all of it, or another amount — which the form adds. An age is never offered under the name "Overdue".
 */
export function recordAmountChoices(answer: OverdueAnswer, balance: number): AmountChoice[] {
    if (!(balance > 0) || answer.kind === 'credit' || answer.kind === 'settled') return [];
    const all: AmountChoice = { key: 'all', label: 'All', amount: fromPaise(toPaise(balance)) };
    if (answer.kind === 'bills' && answer.overdue.amount > 0) {
        const overdue = fromPaise(toPaise(Math.min(answer.overdue.amount, balance)));
        return overdue < all.amount ? [{ key: 'overdue', label: 'Overdue', amount: overdue }, all] : [{ key: 'overdue', label: 'Overdue', amount: overdue }];
    }
    if (answer.kind === 'sheet' && answer.pastCriticalAge.amount > 0 && answer.pastCriticalAge.amount < balance) {
        return [all, { key: 'critical_age', label: `Past critical age · over ${answer.pastCriticalAge.overDays} days`, amount: fromPaise(toPaise(answer.pastCriticalAge.amount)) }];
    }
    return [all];
}

/* -------------------------------- the timeline ------------------------------- */

export type TimelineKind =
    | 'note' | 'no_answer' | 'declined' | 'visit' | 'dispute' | 'payment'
    | 'promise' | 'promise_kept' | 'promise_not_kept' | 'change' | 'cheque' | 'cheque_cleared' | 'bill_cleared';

/** The Activity filters (02 §5.4). */
export type TimelineGroup = 'calls' | 'promises' | 'money' | 'notes' | 'changes';

export const TIMELINE_GROUPS: readonly { value: TimelineGroup | 'all'; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'calls', label: 'Calls & visits' },
    { value: 'promises', label: 'Promises' },
    { value: 'money', label: 'Money' },
    { value: 'notes', label: 'Notes' },
    { value: 'changes', label: 'Changes' },
];

/** A mark for an event: its shape and its colour, never the colour alone (the title names it). */
export type TimelineMark = 'person' | 'promise' | 'kept' | 'broken' | 'cheque' | 'cleared' | 'change';

export interface TimelineEvent {
    id: string;
    /** An ISO moment, or a calendar day (YYYY-MM-DD) where only the day is known. */
    at: string;
    /** Only the day is known: a bill TallyPrime no longer lists, found at the first update without it. */
    dayOnly: boolean;
    kind: TimelineKind;
    group: TimelineGroup;
    mark: TimelineMark;
    title: string;
    actor: string | null;
    body: string | null;
    amount: number | null;
    /** What the amount is: "promised for 18 Oct", "cheque", "bill closed". */
    amountNote: string | null;
    /** Where a fact came from, when it is not the team's own entry. */
    source: 'TallyPrime' | 'Cheques' | null;
}

const ENTRY: Record<Exclude<ActivityKind, 'promise' | 'system'>, { title: string; group: TimelineGroup; mark: TimelineMark }> = {
    note: { title: 'Note', group: 'notes', mark: 'person' },
    no_answer: { title: 'No answer', group: 'calls', mark: 'person' },
    declined: { title: 'Call declined', group: 'calls', mark: 'person' },
    visit: { title: 'Visited', group: 'calls', mark: 'person' },
    dispute: { title: 'Disputed', group: 'calls', mark: 'person' },
    payment: { title: 'Payment received', group: 'money', mark: 'kept' },
};

export interface TimelineInput {
    entries: readonly ActivityEntry[];
    cheques?: readonly Cheque[];
    /** Bills TallyPrime no longer lists as open (`closedBills`): receipts are not read, so how they were paid is not known. */
    cleared?: readonly { key: string; ref: string; amount: number; clearedOn: string }[];
    /** The day a moment fell on, in the company's zone (YYYY-MM-DD). */
    dayOf: (at: string) => string;
    /** A calendar day as the company writes it ("18 Oct"). */
    short: (date: string) => string;
    today: Date;
}

/**
 * One account's history as one timeline, newest first (01 §10, 02 §5.4): what the team recorded, promises and how
 * they ended, cheques, and the bills TallyPrime shows as closed. Nothing is invented: no receipt (receipts are not
 * read from TallyPrime), no reminder (B2), no ledger entry (Phase D).
 */
export function accountTimeline(input: TimelineInput): TimelineEvent[] {
    const byId = new Map(input.entries.map(e => [e.id, e]));
    const events: TimelineEvent[] = [];
    for (const e of input.entries) {
        const actor = e.authorName || (e.kind === 'system' ? 'Timely Payment' : 'Somebody');
        const body = e.body?.trim() ? e.body.trim() : null;
        const promise = e.resolvesId ? byId.get(e.resolvesId) : undefined;
        if (promise && promise.kind === 'promise') {
            const kept = e.kind === 'payment';
            events.push({
                id: e.id, at: e.createdAt, dayOnly: false, kind: kept ? 'promise_kept' : 'promise_not_kept', group: 'promises',
                mark: kept ? 'kept' : 'broken', title: kept ? 'Promise kept' : 'Promise not kept', actor, body,
                amount: promise.promisedAmount ?? null,
                amountNote: promise.promisedOn ? `promised for ${input.short(promise.promisedOn)}` : 'promised',
                source: null,
            });
            continue;
        }
        if (e.kind === 'promise') {
            events.push({
                id: e.id, at: e.createdAt, dayOnly: false, kind: 'promise', group: 'promises', mark: 'promise', title: 'Promised to pay',
                actor, body, amount: e.promisedAmount ?? null, amountNote: e.promisedOn ? `by ${input.short(e.promisedOn)}` : 'no date given', source: null,
            });
            continue;
        }
        if (e.kind === 'system') {
            events.push({ id: e.id, at: e.createdAt, dayOnly: false, kind: 'change', group: 'changes', mark: 'change', title: 'Account change', actor, body, amount: null, amountNote: null, source: null });
            continue;
        }
        const k = ENTRY[e.kind];
        events.push({ id: e.id, at: e.createdAt, dayOnly: false, kind: e.kind, group: k.group, mark: k.mark, title: k.title, actor, body, amount: null, amountNote: null, source: null });
    }
    for (const c of input.cheques ?? []) {
        const state = chequeState(c, input.today);
        const words = [c.bankName, c.chequeNumber, `dated ${input.short(dayValue(c.chequeDate))}`, CHEQUE_WORDS[state]]
            .filter(Boolean).join(' · ');
        const recorded = dayValue(c.receivedDate);
        if (recorded && recorded !== '1970-01-01') {
            events.push({
                id: `cheque:${c.id}`, at: new Date(c.receivedDate).toISOString(), dayOnly: false, kind: 'cheque', group: 'money', mark: 'cheque',
                title: 'Cheque recorded', actor: null, body: words, amount: Math.abs(c.amount), amountNote: 'cheque', source: 'Cheques',
            });
        }
        if (state === 'cleared' && c.clearedDate) {
            events.push({
                id: `cheque-cleared:${c.id}`, at: new Date(c.clearedDate).toISOString(), dayOnly: false, kind: 'cheque_cleared', group: 'money', mark: 'kept',
                title: 'Cheque cleared', actor: null, body: [c.bankName, c.chequeNumber].filter(Boolean).join(' · ') || null,
                amount: Math.abs(c.amount), amountNote: 'cheque', source: 'Cheques',
            });
        }
    }
    for (const b of input.cleared ?? []) {
        events.push({
            id: `cleared:${b.key}:${b.clearedOn}`, at: b.clearedOn, dayOnly: true, kind: 'bill_cleared', group: 'money', mark: 'cleared',
            title: 'Bill cleared', actor: null,
            body: `${b.ref || 'A bill'} is no longer open in TallyPrime. Receipts are not read from TallyPrime, so Timely does not say how it was paid.`,
            amount: b.amount, amountNote: 'bill closed', source: 'TallyPrime',
        });
    }
    // Newest first. A day-only fact sorts after the moments of its own day: its time is not known.
    const key = (ev: TimelineEvent) => (ev.dayOnly ? `${ev.at}T00:00:00.000Z` : ev.at);
    const dayKey = (ev: TimelineEvent) => (ev.dayOnly ? ev.at : input.dayOf(ev.at));
    return events.sort((a, b) => {
        const da = dayKey(a), db = dayKey(b);
        if (da !== db) return da < db ? 1 : -1;
        if (a.dayOnly !== b.dayOnly) return a.dayOnly ? 1 : -1;
        const ka = key(a), kb = key(b);
        return ka < kb ? 1 : ka > kb ? -1 : a.id < b.id ? 1 : -1;
    });
}

/** Where a cheque stands, as its timeline line ends: in the register's own terms (`CHEQUE_STATES`). */
const CHEQUE_WORDS: Record<ReturnType<typeof chequeState>, string> = {
    upcoming: 'in hand', due: `in hand, ${CHEQUE_STATES.due.label.toLowerCase()}`, overdue: `in hand, ${CHEQUE_STATES.overdue.label.toLowerCase()}`,
    hold: CHEQUE_STATES.hold.label.toLowerCase(), cleared: CHEQUE_STATES.cleared.label.toLowerCase(), bounced: CHEQUE_STATES.bounced.label.toLowerCase(),
};

const dayValue = (value: Date | string): string => {
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

/** The events of one filter. */
export const timelineOf = (events: readonly TimelineEvent[], group: TimelineGroup | 'all'): TimelineEvent[] =>
    group === 'all' ? [...events] : events.filter(e => e.group === group);

/* ------------------------------ concentration ------------------------------ */

/**
 * "The 5 largest accounts hold 44% of what is owed" (02 §7): a fixed sentence over stored facts. `owed` is what is
 * owed to the company (debit balances, credits left out); `largest` the largest debit balances.
 */
export function concentration(largest: readonly { amount: number }[], owed: number): { count: number; amount: number; share: number; percent: string } | null {
    const debits = largest.map(a => Math.max(0, Number(a.amount) || 0)).filter(a => a > 0);
    if (!(owed > 0) || debits.length < 2) return null;
    const amount = fromPaise(debits.reduce((s, a) => s + toPaise(a), 0));
    const share = Math.min(1, amount / owed);
    return { count: debits.length, amount, share, percent: percentWords(share) };
}

/** A share as a percentage: no decimals; a share that rounds to nothing is "under 1%". */
export const percentWords = (share: number): string => {
    const p = Math.round(share * 100);
    return p === 0 && share > 0 ? 'under 1%' : `${p}%`;
};

/* ----------------------------------- money ---------------------------------- */

/**
 * A balance in words beside an exact amount (02 §11): "₹1,50,000 Dr", or "In credit ₹18,500". A Dr/Cr marker is
 * only ever set beside an exact figure, so "Cr" (credit) can never be read as "Cr" (crore).
 */
export function balanceWords(total: number, type: BalanceType | undefined, exact: (amount: number) => string): string {
    const amount = Math.abs(Number(total) || 0);
    if (amount === 0) return exact(0);
    return type === 'Cr' ? `In credit ${exact(amount)}` : `${exact(amount)} Dr`;
}
